import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useMyPermissions from "../hooks/useMyPermissions";
import ModalCloseX from "../components/common/ModalCloseX";
import {
  listSalonUsers,
  addSalonUser,
  updateSalonUser,
  removeSalonUser,
} from "../api/salonUsers";
import { listStaff } from "../api/staffs";
import {
  getPermissionMatrix,
  getUserPermissions,
  updateUserPermissions,
  resetUserPermissions,
} from "../api/salonPermissions";
import { formatPermissionError } from "../utils/permissionErrorMessages";

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "manager", label: "Manager" },
  { value: "receptionist", label: "Receptionist" },
  { value: "technician", label: "Technician" },
  { value: "apprentice", label: "Apprentice" },
];

const OWNER_ONLY_PERMISSIONS = ["settings.manage", "users.manage_roles"];

const ROLE_HEADERS = [
  { value: "owner", label: "Owner" },
  { value: "manager", label: "Manager" },
  { value: "receptionist", label: "Receptionist" },
  { value: "technician", label: "Technician" },
  { value: "apprentice", label: "Apprentice" },
];

const PERMISSION_GROUPS = [
  {
    title: "Scheduling",
    items: [
      { key: "schedule.view_all", label: "See the full schedule" },
      { key: "schedule.view_own", label: "See your own schedule" },
      { key: "schedule.edit", label: "Create or edit appointments" },
      { key: "schedule.cancel", label: "Cancel appointments" },
    ],
  },
  {
    title: "Time Clock",
    items: [
      { key: "timeclock.view", label: "View and stamp time clocks" },
      { key: "timeclock.edit", label: "Edit time clock entries" },
      { key: "timeclock.delete", label: "Delete time clock entries" },
    ],
  },
  {
    title: "Clients",
    items: [
      { key: "clients.manage", label: "Manage client records" },
      { key: "clients.view_contact", label: "View client contact info" },
      { key: "clients.view_metrics", label: "View client visit and spend metrics" },
      {
        key: "client_portal.manage_registrations",
        label: "Approve portal registrations",
        matrixOnly: true,
        roleDefaults: {
          owner: true,
          manager: true,
          receptionist: true,
          technician: false,
          apprentice: false,
        },
      },
    ],
  },
  {
    title: "Emails",
    items: [
      {
        key: "emails.manage_opt_outs",
        label: "Manage email opt-outs and preferences",
      },
    ],
  },
  {
    title: "Tickets",
    items: [
      { key: "tickets.manage", label: "Manage tickets" },
      { key: "tickets.delete", label: "Delete tickets" },
      { key: "tickets.reopen", label: "Re-open tickets" },
      { key: "tickets.refund", label: "Refund tickets" },
    ],
  },
  {
    title: "Services",
    items: [{ key: "services.manage", label: "Manage services" }],
  },
  {
    title: "Workspace Rentals",
    items: [
      { key: "rentals.view", label: "View workspace rentals" },
      { key: "rentals.manage", label: "Manage workspace rentals" },
      { key: "rentals.marketplace_manage", label: "Manage marketplace listings" },
      { key: "rentals.reports.view", label: "View rental reports" },
    ],
  },
  {
    title: "Staff",
    items: [{ key: "staff.manage", label: "Manage staff members" }],
  },
  {
    title: "Settings",
    items: OWNER_ONLY_PERMISSIONS.map((key) => ({
      key,
      label: key === "settings.manage" ? "Manage salon settings" : "Manage user roles",
    })),
  },
  {
    title: "Rewards",
    items: [{ key: "rewards.view", label: "View rewards program" }],
  },
  {
    title: "Reports",
    items: [
      { key: "reports.view", label: "View reports" },
      { key: "schedule.estimated_revenue", label: "View estimated revenue" },
    ],
  },
  {
    title: "Payroll",
    items: [
      { key: "payroll.view", label: "View payroll" },
      { key: "payroll.manage", label: "Manage payroll and pay stubs" },
    ],
  },
  {
    title: "Training & Development",
    items: [
      { key: "training.view", label: "View assigned training" },
      { key: "training.manage", label: "Create modules and assign training" },
    ],
  },
];

const formatBool = (value) => (value ? "Yes" : "No");

const ERROR_MESSAGES = {
  USER_NOT_FOUND: "User not found. Ask them to sign up first.",
};
const OWNER_REQUIRED_ERROR = "At least one user with Owner role is required for the salon.";

export default function ManageUsersRoles() {
  const navigate = useNavigate();
  const { token, role, user } = useAuth();
  const normalizedRole = (role || "").toLowerCase();
  const { permissions: routePermissions, loading: routePermissionsLoading } =
    useMyPermissions({ enabled: Boolean(token) });
  const canAccessUserRoles =
    ["admin", "host"].includes(normalizedRole) ||
    Boolean(user?.is_super_admin) ||
    (routePermissions === null
      ? ["admin", "host", "owner", "manager", "solo_technician", "reception"].includes(
          normalizedRole
        )
      : Boolean(routePermissions?.["users.manage_roles"]));

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pendingChanges, setPendingChanges] = useState({});
  const [rowSaving, setRowSaving] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [form, setForm] = useState({ email: "", role: "technician" });
  const [formError, setFormError] = useState("");
  const [adding, setAdding] = useState(false);
  const [permissionMatrix, setPermissionMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(true);
  const [matrixError, setMatrixError] = useState("");
  const [selectedUserForPerms, setSelectedUserForPerms] = useState(null);
  const [permsBundle, setPermsBundle] = useState(null);
  const [localOverrides, setLocalOverrides] = useState({});
  const [permsLoading, setPermsLoading] = useState(false);
  const [permsError, setPermsError] = useState("");
  const [permsStatus, setPermsStatus] = useState("");
  const [permsSaving, setPermsSaving] = useState(false);
  const [permsResetting, setPermsResetting] = useState(false);
  const [staffByUserId, setStaffByUserId] = useState({});

  const roleOptions = ROLE_OPTIONS;

  useEffect(() => {
    if (!token) {
      navigate("/login?next=/manager/manage/users", { replace: true });
      return;
    }
    if (routePermissionsLoading || routePermissions === null) {
      return;
    }
    if (!canAccessUserRoles) {
      navigate("/my-bookings", { replace: true });
    }
  }, [
    token,
    navigate,
    routePermissionsLoading,
    routePermissions,
    canAccessUserRoles,
  ]);

  useEffect(() => {
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadStaff = async () => {
      if (!token) return;
      try {
        const data = await listStaff({}, token);
        const map = {};
        (Array.isArray(data) ? data : []).forEach((staff) => {
          if (staff?.user_id) {
            map[staff.user_id] = staff.id;
          }
        });
        setStaffByUserId(map);
      } catch (_) {
        setStaffByUserId({});
      }
    };
    loadStaff();
  }, [token]);

  useEffect(() => {
    const loadMatrix = async () => {
      if (!token) {
        setMatrixLoading(false);
        return;
      }
      setMatrixLoading(true);
      setMatrixError("");
      try {
        const data = await getPermissionMatrix(token);
        setPermissionMatrix(data);
      } catch (err) {
        setMatrixError("Unable to load permission matrix");
        setPermissionMatrix(null);
      } finally {
        setMatrixLoading(false);
      }
    };
    loadMatrix();
  }, [token]);

  const loadMembers = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listSalonUsers(token);
      setMembers(Array.isArray(data) ? data : []);
      setPendingChanges({});
    } catch (err) {
      setError("Unable to load members");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (userId, field, value) => {
    setRowErrors((prev) => ({ ...prev, [userId]: null }));
    setPendingChanges((prev) => {
      const base = members.find((member) => member.user_id === userId);
      if (!base) {
        return prev;
      }
      const existing = prev[userId] || {};
      const updated = { ...existing, [field]: value };
      if (
        updated.role === base.role &&
        updated.is_active === base.is_active
      ) {
        const next = { ...prev };
        delete next[userId];
        return next;
      }
      return { ...prev, [userId]: updated };
    });
  };

  const handleSave = async (member) => {
    const pending = pendingChanges[member.user_id];
    if (!pending) return;
    const roleChanged = pending.role && pending.role !== member.role;
    setRowSaving((prev) => ({ ...prev, [member.user_id]: true }));
    try {
      await updateSalonUser(member.user_id, pending);
      await loadMembers();
      if (
        roleChanged &&
        selectedUserForPerms?.user_id === member.user_id
      ) {
        try {
          const bundle = await getUserPermissions(member.user_id, token);
          setPermsBundle(bundle);
          setLocalOverrides(bundle.overrides ? { ...bundle.overrides } : {});
        } catch (err) {
          setPermsError(
            formatPermissionError(
              err,
              "Unable to reload permissions. Please check your access and try again."
            )
          );
        }
      }
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [member.user_id]:
          formatPermissionError(err, "Unable to save changes. Please try again.") ===
          "At least one active owner must remain on the salon account."
            ? OWNER_REQUIRED_ERROR
            : formatPermissionError(err, "Unable to save changes. Please try again."),
      }));
    } finally {
      setRowSaving((prev) => ({ ...prev, [member.user_id]: false }));
    }
  };

  const handleRemove = async (member) => {
    setRowSaving((prev) => ({ ...prev, [member.user_id]: true }));
    setRowErrors((prev) => ({ ...prev, [member.user_id]: null }));
    try {
      await removeSalonUser(member.user_id);
      await loadMembers();
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [member.user_id]:
          err?.response?.data?.detail || "Unable to remove member",
      }));
    } finally {
      setRowSaving((prev) => ({ ...prev, [member.user_id]: false }));
    }
  };

  const handleAddMember = async (event) => {
    event.preventDefault();
    setFormError("");
    if (!form.email.trim()) {
      setFormError("Email is required");
      return;
    }
    setAdding(true);
    try {
      await addSalonUser({ email: form.email.trim(), role: form.role });
      setForm({ email: "", role: "technician" });
      await loadMembers();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "USER_NOT_FOUND") {
        setFormError(ERROR_MESSAGES.USER_NOT_FOUND);
      } else if (detail === "At least one owner is required for this salon.") {
        setFormError(detail);
      } else {
        setFormError("Unable to add member");
      }
    } finally {
      setAdding(false);
    }
  };

  const openPermissionsDrawer = async (member) => {
    setSelectedUserForPerms(member);
    setPermsError("");
    setPermsStatus("");
    setPermsBundle(null);
    setLocalOverrides({});
    setPermsLoading(true);
    try {
      const bundle = await getUserPermissions(member.user_id, token);
      setPermsBundle(bundle);
      setLocalOverrides(bundle.overrides ? { ...bundle.overrides } : {});
    } catch (err) {
      setPermsError(
        formatPermissionError(
          err,
          "Unable to load permissions. Please check your access and try again."
        )
      );
    } finally {
      setPermsLoading(false);
    }
  };

  const closePermissionsDrawer = () => {
    setSelectedUserForPerms(null);
    setPermsBundle(null);
    setLocalOverrides({});
    setPermsError("");
    setPermsStatus("");
    setPermsSaving(false);
    setPermsResetting(false);
  };

  const effectivePermissions = useMemo(() => {
    const base = { ...(permsBundle?.defaults || {}) };
    Object.entries(localOverrides || {}).forEach(([key, value]) => {
      base[key] = value;
    });
    return base;
  }, [permsBundle?.defaults, localOverrides]);

  const hasPermissionChanges = useMemo(
    () =>
      JSON.stringify(localOverrides || {}) !==
      JSON.stringify(permsBundle?.overrides || {}),
    [localOverrides, permsBundle?.overrides]
  );

  const labelText =
    "text-xs uppercase tracking-wide text-slate-500 font-semibold";
  const fieldClass =
    "h-11 px-4 rounded-xl border border-slate-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10";
  const primaryBtn =
    "h-10 px-5 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800";
  const outlineBtn =
    "h-10 px-5 rounded-full border border-slate-300 text-slate-900 font-semibold hover:bg-slate-50";
  const dangerOutlineBtn =
    "h-10 px-5 rounded-full border border-rose-300 text-rose-600 font-semibold hover:bg-rose-50";
  const sectionCard = "rounded-2xl border border-slate-200 bg-white";
  const sectionHeader =
    "px-5 py-4 border-b border-slate-100 flex items-center justify-between";
  const tableHeadClass =
    "bg-slate-50 text-slate-500 uppercase text-xs tracking-wide";
  const matrixTableStyle = {
    fontSize: "clamp(13px, 0.95vw, 15px)",
    lineHeight: 1.25,
  };
  const matrixHeadStyle = {
    fontSize: "clamp(12px, 0.9vw, 14px)",
    lineHeight: 1.25,
  };
  const permissionColWidth = "40%";
  const roleColWidth = `${(60 / ROLE_HEADERS.length).toFixed(2)}%`;
  const matrixCellClass = "px-3 py-2.5 md:px-4 md:py-3";

  const togglePermission = (key) => {
    setPermsStatus("");
    setLocalOverrides((prev) => {
      const next = { ...(prev || {}) };
      const defaultValue = permsBundle?.defaults?.[key] ?? false;
      const currentValue = effectivePermissions[key];
      const nextValue = !currentValue;
      if (nextValue === defaultValue) {
        delete next[key];
      } else {
        next[key] = nextValue;
      }
      return next;
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedUserForPerms) return;
    setPermsSaving(true);
    setPermsError("");
    setPermsStatus("");
    try {
      const result = await updateUserPermissions(
        selectedUserForPerms.user_id,
        localOverrides,
        token
      );
      setPermsBundle(result);
      setLocalOverrides(result.overrides ? { ...result.overrides } : {});
      setPermsStatus("Permissions saved");
    } catch (err) {
      setPermsError(
        formatPermissionError(
          err,
          "Unable to save permissions. Please check your access and try again."
        )
      );
    } finally {
      setPermsSaving(false);
    }
  };

  const handleResetPermissions = async () => {
    if (!selectedUserForPerms) return;
    setPermsResetting(true);
    setPermsError("");
    setPermsStatus("");
    try {
      const result = await resetUserPermissions(selectedUserForPerms.user_id, token);
      setPermsBundle(result);
      setLocalOverrides(result.overrides ? { ...result.overrides } : {});
      setPermsStatus("Permissions reset");
    } catch (err) {
      setPermsError(
        formatPermissionError(
          err,
          "Unable to reset permissions. Please check your access and try again."
        )
      );
    } finally {
      setPermsResetting(false);
    }
  };

  const scrollToSection = (id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderMembers = () => {
    if (loading) {
      return (
        <tr>
          <td className="px-3 py-2.5 text-slate-500 text-center" colSpan={4}>
            Loading members...
          </td>
        </tr>
      );
    }
    if (!members.length) {
      return (
        <tr>
          <td className="px-3 py-2.5 text-slate-500 text-center" colSpan={4}>
            No members yet.
          </td>
        </tr>
      );
    }
    return members.map((member) => {
      const pending = pendingChanges[member.user_id];
      const currentRole = pending?.role ?? member.role;
      const currentActive =
        pending?.is_active !== undefined
          ? pending.is_active
          : member.is_active;
      const hasChanges =
        pending &&
        (pending.role !== member.role ||
          pending.is_active !== member.is_active);
      const isPrimaryOwner = Boolean(member.is_primary_owner);
      const saving = Boolean(rowSaving[member.user_id]);
      const staffId = staffByUserId[member.user_id];
      return (
        <tr key={member.user_id} className="hover:bg-slate-50">
          <td className="px-3 py-2.5 whitespace-nowrap">
            {staffId ? (
              <Link
                to={`/staff/${staffId}`}
                className="block font-semibold text-slate-900 truncate hover:underline"
                title={member.name}
              >
                {member.name}
              </Link>
            ) : (
              <span
                className="block font-semibold text-slate-900 truncate"
                title={member.name}
              >
                {member.name}
              </span>
            )}
          </td>
          <td className="px-3 py-2.5 whitespace-nowrap">
            <select
              value={currentRole}
              onChange={(event) =>
                handleFieldChange(member.user_id, "role", event.target.value)
              }
              disabled={isPrimaryOwner}
              className="h-9 w-40 rounded-xl border border-slate-300 bg-white px-3 text-[13px] focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </td>
          <td className="px-3 py-2.5 text-center whitespace-nowrap">
            <input
              type="checkbox"
              checked={Boolean(currentActive)}
              onChange={(event) =>
                handleFieldChange(member.user_id, "is_active", event.target.checked)
              }
              disabled={isPrimaryOwner}
              className="h-4 w-4 text-slate-900 border-slate-300 rounded"
            />
          </td>
          <td className="px-3 py-2.5 whitespace-nowrap">
            <div className="flex flex-nowrap items-center gap-2">
              <button
                type="button"
                onClick={() => handleSave(member)}
                disabled={!hasChanges || saving || isPrimaryOwner}
                className={`${primaryBtn} h-9 px-4 text-sm disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              {!isPrimaryOwner && (
                <button
                  type="button"
                  onClick={() => handleRemove(member)}
                  disabled={saving}
                  className={`${dangerOutlineBtn} h-9 px-4 text-sm disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  Remove
                </button>
              )}
              <button
                type="button"
                onClick={() => openPermissionsDrawer(member)}
                disabled={isPrimaryOwner}
                className={`${outlineBtn} h-9 px-4 text-sm disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                Permissions
              </button>
            </div>
            {rowErrors[member.user_id] && (
              <div className="text-xs text-rose-600 mt-1">
                {rowErrors[member.user_id]}
              </div>
            )}
          </td>
        </tr>
      );
    });
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="manageSectionShell">
      <div className="manageSectionWide manage-section-wide manage-section-compact">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="bg-rose-500 px-6 py-5 relative">
          <h1 className="text-2xl font-semibold text-white">Users &amp; Roles</h1>
          <p className="text-white/90 mt-1">
            Manage access and permissions
          </p>
          <div
            className="absolute right-4 top-3 z-50"
            onClick={handleClose}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <ModalCloseX onClick={handleClose} />
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-slate-400">
              Quick links
            </span>
            <button
              type="button"
              onClick={() => scrollToSection("members")}
              className="h-9 px-4 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
            >
              Go to Members
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("add-member")}
              className="h-9 px-4 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
            >
              Add Member
            </button>
          </div>
          <div className={sectionCard}>
            <div className={sectionHeader}>
              <div className="text-lg font-semibold text-slate-900">
                Role Permission Matrix
              </div>
              <span className="text-xs text-slate-500">Defaults by role</span>
            </div>
            <div className="px-5 py-4">
              {matrixLoading ? (
                <p className="text-sm text-slate-500">Loading permission matrix...</p>
              ) : matrixError ? (
                <p className="text-sm text-rose-600">{matrixError}</p>
              ) : permissionMatrix ? (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="w-full table-fixed" style={matrixTableStyle}>
                    <colgroup>
                      <col style={{ width: permissionColWidth }} />
                      {ROLE_HEADERS.map((header) => (
                        <col key={`${header.value}-col`} style={{ width: roleColWidth }} />
                      ))}
                    </colgroup>
                    <thead className={tableHeadClass} style={matrixHeadStyle}>
                      <tr>
                        <th className={`${matrixCellClass} text-left align-middle`}>
                          Permission
                        </th>
                        {ROLE_HEADERS.map((header) => (
                          <th
                            key={header.value}
                            className={`${matrixCellClass} text-center align-middle whitespace-nowrap`}
                          >
                            {header.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {PERMISSION_GROUPS.map((group) => (
                        <Fragment key={group.title}>
                          <tr>
                            <td
                              colSpan={ROLE_HEADERS.length + 1}
                              className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500 bg-slate-50"
                            >
                              {group.title}
                            </td>
                          </tr>
                          {group.items.map((item) => (
                            <tr key={item.key} className="border-b border-slate-100">
                              <td className={`${matrixCellClass} text-left align-middle`}>
                                <span className="block truncate" title={item.label}>
                                  {item.label}
                                </span>
                              </td>
                              {ROLE_HEADERS.map((header) => {
                                const value = item.matrixOnly
                                  ? item.roleDefaults?.[header.value]
                                  : permissionMatrix.role_defaults?.[header.value]?.[item.key];
                                const valueClass = value
                                  ? "text-slate-900 font-semibold"
                                  : "text-slate-400";
                                return (
                                  <td
                                    key={`${item.key}-${header.value}`}
                                    className={`${matrixCellClass} text-center align-middle whitespace-nowrap`}
                                  >
                                    <span
                                      className={`${valueClass} inline-block w-full text-center`}
                                    >
                                      {formatBool(value)}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Permission data unavailable.</p>
              )}
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 text-sm">
              {error}
            </div>
          )}

          <div id="members" className={`${sectionCard} scroll-mt-24`}>
            <div className={sectionHeader}>
              <div className="text-lg font-semibold text-slate-900">Members</div>
              <span className="text-xs text-slate-500">{members.length} total</span>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-slate-200 mx-5 my-4">
              <table className="w-full table-fixed text-[14px] leading-tight">
                <colgroup>
                  <col style={{ width: "34%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "34%" }} />
                </colgroup>
                <thead className={tableHeadClass}>
                  <tr>
                    <th className="px-3 py-2.5 text-left whitespace-nowrap">
                      Name
                    </th>
                    <th className="px-3 py-2.5 text-left whitespace-nowrap">
                      Role
                    </th>
                    <th className="px-3 py-2.5 text-center whitespace-nowrap">
                      Active
                    </th>
                    <th className="px-3 py-2.5 text-left whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {renderMembers()}
                </tbody>
              </table>
            </div>
          </div>

          <div id="add-member" className={`${sectionCard} px-5 py-4 space-y-4 scroll-mt-24`}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Add Member</h2>
              <span className="text-xs uppercase tracking-wide text-slate-400">
                Invite by email
              </span>
            </div>
            {formError && (
              <div className="text-sm text-rose-600">{formError}</div>
            )}
            <form className="grid gap-4 md:grid-cols-3 items-end" onSubmit={handleAddMember}>
              <label className="space-y-1">
                <span className={labelText}>Email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, email: event.target.value }))
                  }
                  className={`w-full ${fieldClass}`}
                  placeholder="user@example.com"
                  required
                />
              </label>
              <label className="space-y-1">
                <span className={labelText}>Role</span>
                <select
                  value={form.role}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, role: event.target.value }))
                  }
                  className={`w-full ${fieldClass} bg-white`}
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="submit"
                disabled={adding}
                className={`${primaryBtn} w-full md:w-auto disabled:opacity-60`}
              >
                {adding ? "Adding..." : "Add Member"}
              </button>
            </form>
          </div>
        </div>
      </div>
      {selectedUserForPerms && (
        <div className="fixed inset-0 z-40 flex items-end md:items-center justify-center px-4 py-6">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={closePermissionsDrawer}
          />
          <div className="relative w-full max-w-3xl bg-white rounded-t-xl md:rounded-xl shadow-xl overflow-hidden">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Permissions</h2>
                <p className="text-sm text-slate-500">
                  {selectedUserForPerms.name}
                </p>
                <p className="text-xs text-slate-500">
                  Role: {permsBundle?.role || selectedUserForPerms.role}
                </p>
              </div>
              <button
                type="button"
                onClick={closePermissionsDrawer}
                className="text-sm font-semibold text-slate-500 hover:text-slate-900"
              >
                Close
              </button>
            </div>
            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-4">
              {permsLoading ? (
                <p className="text-sm text-slate-500">Loading permissions...</p>
              ) : permsError ? (
                <p className="text-sm text-rose-600">{permsError}</p>
              ) : (
                <>
                  {PERMISSION_GROUPS.map((group) => (
                    <div key={group.title} className="space-y-2">
                      <div className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
                        {group.title}
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {group.items
                          .filter((item) => !item.matrixOnly)
                          .map((item) => {
                          const disabled = false;
                          const defaultValue =
                            permsBundle?.defaults?.[item.key] ?? false;
                          const effectiveValue =
                            effectivePermissions[item.key] ?? defaultValue;
                          return (
                            <label
                              key={item.key}
                              className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${disabled
                                  ? "border-slate-200 bg-slate-50 text-slate-400"
                                  : "border-slate-200 bg-white text-slate-700"
                                }`}
                            >
                              <div className="flex flex-col">
                                <span>{item.label}</span>
                                <span className="text-xs text-slate-500">
                                  Default: {formatBool(defaultValue)} · Effective:{" "}
                                  {formatBool(effectiveValue)}
                                </span>
                              </div>
                              <input
                                type="checkbox"
                                checked={Boolean(effectiveValue)}
                                onChange={() => togglePermission(item.key)}
                                disabled={
                                  disabled || permsSaving || permsResetting
                                }
                                className="h-4 w-4 text-emerald-600 border-slate-300 rounded"
                              />
                            </label>
                          );
                          })}
                      </div>
                    </div>
                  ))}
                  {permsStatus && (
                    <p className="text-sm text-emerald-600">{permsStatus}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleSavePermissions}
                      disabled={
                        !hasPermissionChanges ||
                        permsSaving
                      }
                      className="px-4 py-2 rounded bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60"
                    >
                      {permsSaving ? "Saving..." : "Save permissions"}
                    </button>
                    <button
                      type="button"
                      onClick={handleResetPermissions}
                      disabled={permsResetting}
                      className="px-4 py-2 rounded border border-slate-300 text-sm font-semibold text-slate-700 disabled:opacity-60"
                    >
                      {permsResetting ? "Resetting..." : "Reset permissions"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
